import { useLayoutEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { PerspectiveCamera } from 'three';

interface CameraSetterProps {
  near?: number;
  far?: number;
  fov?: number;
  position?: [number, number, number];
  lookAt?: [number, number, number];
}

// Configures the PersistentCanvas's shared default camera for the currently
// active scene. We deliberately mutate the shared camera instead of using
// drei's <PerspectiveCamera makeDefault /> here — that pattern has a race
// with tunnel-rat where both scenes' makeDefault setup/cleanup run in the
// same React commit during a route change, leaving the camera in the wrong
// state. Mutating the shared camera mirrors what ME3DScene already does
// frame-by-frame via state.camera.position.copy(...).
export const CameraSetter = ({ near, far, fov, position, lookAt }: CameraSetterProps) => {
  const camera = useThree((state) => state.camera) as PerspectiveCamera;
  const invalidate = useThree((state) => state.invalidate);

  useLayoutEffect(() => {
    let projectionDirty = false;

    if (near !== undefined && camera.near !== near) {
      camera.near = near;
      projectionDirty = true;
    }
    if (far !== undefined && camera.far !== far) {
      camera.far = far;
      projectionDirty = true;
    }
    if (fov !== undefined && camera.fov !== fov) {
      camera.fov = fov;
      projectionDirty = true;
    }
    if (position) {
      camera.position.set(position[0], position[1], position[2]);
    }
    if (lookAt) {
      camera.lookAt(lookAt[0], lookAt[1], lookAt[2]);
    }
    if (projectionDirty) {
      camera.updateProjectionMatrix();
    }
    // We're in demand mode — request a frame so the canvas paints with the
    // new camera state immediately after a route change.
    invalidate();
  }, [camera, invalidate, near, far, fov, position?.[0], position?.[1], position?.[2], lookAt?.[0], lookAt?.[1], lookAt?.[2]]);

  return null;
};

export default CameraSetter;
